import { DialogDescription } from "@radix-ui/react-dialog";
import { DownloadIcon } from "lucide-react";
import { useEffect, useMemo } from "react";
import DocViewer, { DocViewerRenderers } from "react-doc-viewer";
import { useTranslation } from "react-i18next";

import { useLazyGetDocumentFileQuery } from "src/core/api/documents";
import { SpinnerFullPage } from "src/core/components/custom-ui/spinner-full-page";
import { Button } from "src/core/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "src/core/components/ui/dialog";

export const DocumentPreviewModal = ({
  doc,
  open,
  handleOpenChange,
}: {
  doc: { id: string; name: string };
  open: boolean;
  handleOpenChange: (open: boolean) => void;
}) => {
  const [trigger, { data: blob, isFetching }] = useLazyGetDocumentFileQuery();
  const { t } = useTranslation();
  useEffect(() => {
    if (open && doc?.id) {
      trigger({ documentId: doc.id });
    }
  }, [open, doc?.id, trigger]);

  const objectUrl = useMemo(() => (blob ? URL.createObjectURL(blob) : ""), [blob]);
  const fileType = blob?.type?.split("/")?.[1] ?? "";

  useEffect(() => {
    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [objectUrl]);

  const handleDownload = () => {
    if (!objectUrl) return;
    const link = document.createElement("a");
    link.href = objectUrl;
    link.download = doc.name || "document";
    link.click();
  };

  const isPdf = blob?.type === "application/pdf";

  return (
    <Dialog open={open} onOpenChange={(open) => handleOpenChange(open)}>
      <DialogContent className="w-[75%] max-h-[90vh] overflow-auto">
        <DialogHeader className="flex items-center justify-between">
          <DialogTitle>{doc.name}</DialogTitle>
          <DialogDescription className="sr-only">
            {t("documentPreview.modal.description")}
          </DialogDescription>
        </DialogHeader>
        {isFetching && (
          <div className="flex items-center justify-center h-full">
            <SpinnerFullPage />
          </div>
        )}
        {blob && !isFetching && (
          <div className="h-[75vh]">
            {isPdf ? (
              <iframe
                src={objectUrl}
                className="w-full h-full rounded-lg border"
                title="aperçu PDF"
              />
            ) : (
              <>
                <div className="w-full flex items-center justify-center mb-10">
                  <Button
                    className="flex flex-row items-center gap-x-10"
                    variant="link"
                    onClick={handleDownload}
                  >
                    <DownloadIcon className="text-blue-info h-20 w-20" />
                    {t("download")}
                  </Button>
                </div>
                <DocViewer
                  documents={[{ uri: objectUrl, fileType }]}
                  pluginRenderers={DocViewerRenderers}
                  config={{
                    header: { disableHeader: true },
                  }}
                />
              </>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
